import React, { useState, useEffect } from 'react';
import {
  Tabs, Card, Row, Col, Statistic, Form, InputNumber, Button, message,
  Table, Tag, Space, Modal, Select, Radio, Input, Descriptions, Popconfirm,
  Typography, Alert, Spin, Switch
} from 'antd';
import {
  DollarOutlined, LockOutlined, UnlockOutlined, EditOutlined, EyeOutlined, ReloadOutlined,
  SyncOutlined, HistoryOutlined
} from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

// ─── واحد پول جادوگری: 1 گالیون = 17 سیکل = 493 نات ──────────────────────────
const KNUTS_PER_SICKLE = 29;
const SICKLES_PER_GALLEON = 17;
const KNUTS_PER_GALLEON = SICKLES_PER_GALLEON * KNUTS_PER_SICKLE; // 493

function formatKnuts(totalKnuts = 0) {
  const safe = Number(totalKnuts) || 0;
  const g = Math.floor(safe / KNUTS_PER_GALLEON);
  const rem = safe % KNUTS_PER_GALLEON;
  const s = Math.floor(rem / KNUTS_PER_SICKLE);
  const k = rem % KNUTS_PER_SICKLE;
  const parts = [];
  if (g) parts.push(`${g}G`);
  if (s) parts.push(`${s}S`);
  if (k || parts.length === 0) parts.push(`${k}K`);
  return parts.join(' ');
}

const TX_TYPE_LABELS = {
  train_ticket: 'بلیت قطار',
  full_package: 'بسته کامل ورود',
  gift_package: 'بسته هدیه',
  requirements: 'خرید وسایل مدرسه',
  initial_galleons: 'گالیون اولیه',
  galleon_pack: 'بسته گالیون',
  galleon_exchange: 'صرافی گرینگاتس',
  vault_to_wallet: 'انتقال خزانه ← کیف پول',
  wallet_to_vault: 'انتقال کیف پول ← خزانه',
  shop_purchase: 'خرید از فروشگاه',
  admin_adjustment: 'تعدیل دستی ادمین',
  refund: 'استرداد'
};

const STATUS_LABELS = {
  pending: { text: 'در انتظار', color: 'gold' },
  completed: { text: 'تکمیل‌شده', color: 'green' },
  failed: { text: 'ناموفق', color: 'red' },
  refunded: { text: 'مسترد شده', color: 'blue' }
};

const GATEWAY_LABELS = {
  zarinpal: 'زرین‌پال',
  zibal: 'زیبال',
  oxapay: 'OxaPay (کریپتو)'
};

export default function GringottsBankManager() {
  return (
    <Card>
      <Tabs
        defaultActiveKey="rate"
        items={[
          { key: 'rate', label: 'نرخ ارز', children: <ExchangeRateTab /> },
          { key: 'stats', label: 'آمار بانک', children: <BankStatsTab /> },
          { key: 'vaults', label: 'خزانه‌ها', children: <VaultsTab /> },
          { key: 'transactions', label: 'تراکنش‌ها', children: <TransactionsTab /> }
        ]}
      />
    </Card>
  );
}

// ─── تب ۱: نرخ ارز ────────────────────────────────────────────────────────────
function ExchangeRateTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getExchangeRate();
      if (res.data.success) {
        setRate(res.data.rate);
        form.setFieldsValue({
          tomanPerPound: res.data.rate.tomanPerPound,
          tomanPerUsd: res.data.rate.tomanPerUsd || undefined
        });
      }
    } catch {
      message.error('خطا در دریافت نرخ ارز');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async ({ tomanPerPound, tomanPerUsd }) => {
    setSaving(true);
    try {
      const res = await adminAPI.updateExchangeRate(tomanPerPound, tomanPerUsd);
      if (res.data.success) {
        message.success('نرخ ارز به‌روزرسانی شد');
        setRate(res.data.rate);
      } else {
        message.error(res.data.message || 'خطا در ذخیره نرخ');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در ذخیره نرخ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <Alert
        type="info"
        showIcon
        icon={<DollarOutlined />}
        style={{ marginBottom: 20 }}
        message="نرخ تبدیل گالیون"
        description="قیمت هر گالیون از ضرب «پوند به ازای هر گالیون» (ثابت) در نرخ پوند محاسبه می‌شود. «تومان به ازای هر پوند» برای خرید گالیون با تومان (زرین‌پال/زیبال) استفاده می‌شود. برای پرداخت تتری (OxaPay) دیگر نیازی به وارد کردن مستقیم نرخ دلار به پوند نیست — کافی است «تومان به ازای هر دلار» را هم وارد کنید تا نرخ دلاری خودکار از این دو نرخ محاسبه شود."
      />
      <Card loading={loading} size="small">
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 20 }}>
          <Descriptions.Item label="پوند به ازای هر گالیون (ثابت)">
            {rate?.poundPerGalleon ?? '-'} پوند
          </Descriptions.Item>
          <Descriptions.Item label="قیمت هر گالیون — تومانی">
            {rate ? `${Number(rate.tomanPerGalleon).toLocaleString('fa-IR')} تومان` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="قیمت هر گالیون — دلاری (تتر/OxaPay، محاسبه‌شده)">
            {rate && rate.usdPerGalleon > 0 ? `${Number(rate.usdPerGalleon).toLocaleString('fa-IR')} $` : 'تنظیم نشده'}
          </Descriptions.Item>
        </Descriptions>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="tomanPerPound"
            label="هر پوند چند تومان است؟"
            rules={[
              { required: true, message: 'نرخ را وارد کنید' },
              { type: 'number', min: 1, message: 'نرخ باید بزرگتر از صفر باشد' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              step={1000}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/,/g, '')}
            />
          </Form.Item>
          <Form.Item
            name="tomanPerUsd"
            label="هر دلار چند تومان است؟ (برای محاسبه‌ی خودکار نرخ تتر / OxaPay)"
            rules={[
              { type: 'number', min: 0, message: 'نرخ باید صفر یا بزرگتر باشد' }
            ]}
            extra="اگر خالی بماند، خرید گالیون با تتر تا وقتی این نرخ تنظیم نشود امکان‌پذیر نخواهد بود."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/,/g, '')}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} icon={<DollarOutlined />}>
            ذخیره نرخ
          </Button>
        </Form>
      </Card>

      <NavasanAutoUpdateCard onRateUpdated={load} />
      <ExchangeRateHistoryCard />
    </div>
  );
}

// ─── به‌روزرسانی خودکار نرخ از نوسان (navasan.tech) ───────────────────────────
function NavasanAutoUpdateCard({ onRateUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getNavasanSettings();
      if (res.data.success) {
        setSettings(res.data.settings);
        form.setFieldsValue({
          apiKey: '', // کلید قبلی نمایش داده نمی‌شود، فقط ماسک آن — برای تغییر باید دوباره وارد شود
          autoUpdateEnabled: res.data.settings.autoUpdateEnabled,
          updateHours: res.data.settings.updateHours
        });
      }
    } catch {
      message.error('خطا در دریافت تنظیمات نوسان');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async ({ apiKey, autoUpdateEnabled, updateHours }) => {
    setSaving(true);
    try {
      const res = await adminAPI.updateNavasanSettings({
        apiKey: apiKey || undefined, // خالی یعنی «تغییر نده»
        autoUpdateEnabled,
        updateHours
      });
      if (res.data.success) {
        message.success('تنظیمات نوسان ذخیره شد');
        setSettings(res.data.settings);
        form.setFieldsValue({ apiKey: '' });
      } else {
        message.error(res.data.message || 'خطا در ذخیره تنظیمات');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const fetchNow = async () => {
    setFetching(true);
    try {
      const res = await adminAPI.fetchExchangeRateNow();
      if (res.data.success) {
        message.success(`نرخ به‌روز شد — پوند: ${Number(res.data.tomanPerPound).toLocaleString('fa-IR')} تومان، دلار: ${Number(res.data.tomanPerUsd).toLocaleString('fa-IR')} تومان`);
        onRateUpdated && onRateUpdated();
      } else {
        message.error(res.data.message || 'خطا در دریافت نرخ از نوسان');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در دریافت نرخ از نوسان');
    } finally {
      setFetching(false);
    }
  };

  const hourOptions = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${h}:۰۰` }));

  return (
    <Card
      loading={loading}
      size="small"
      title="به‌روزرسانی خودکار از نوسان (navasan.tech)"
      style={{ marginTop: 20 }}
      extra={
        <Button icon={<SyncOutlined />} loading={fetching} onClick={fetchNow}>
          دریافت الان
        </Button>
      }
    >
      <Descriptions column={1} bordered size="small" style={{ marginBottom: 20 }}>
        <Descriptions.Item label="کلید API فعلی">
          {settings?.apiKeyMasked || 'تنظیم نشده'}
        </Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="apiKey" label="کلید API نوسان" extra="برای حفظ کلید فعلی خالی بگذارید — فقط برای تغییر کلید پر کنید.">
          <Input placeholder="مثلاً free8M9llcy7QiETI6twuf3Uolhf8yom" />
        </Form.Item>
        <Form.Item name="autoUpdateEnabled" label="به‌روزرسانی خودکار" valuePropName="checked">
          <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
        </Form.Item>
        <Form.Item
          name="updateHours"
          label="ساعت‌های به‌روزرسانی در روز (به وقت سرور)"
          rules={[{ required: true, message: 'حداقل یک ساعت را انتخاب کنید' }]}
        >
          <Select mode="multiple" options={hourOptions} placeholder="مثلاً 9 و 21 برای دو بار در روز" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          ذخیره تنظیمات
        </Button>
      </Form>
    </Card>
  );
}

// ─── گزارش تاریخچه‌ی نرخ ارز ──────────────────────────────────────────────────
function ExchangeRateHistoryCard() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (page) => {
    setLoading(true);
    try {
      const res = await adminAPI.getExchangeRateHistory(page, 10);
      if (res.data.success) {
        setItems(res.data.items);
        setPagination({ page: res.data.pagination.page, limit: res.data.pagination.limit, total: res.data.pagination.total });
      }
    } catch {
      message.error('خطا در دریافت تاریخچه نرخ ارز');
    } finally {
      setLoading(false);
    }
  };

  const SOURCE_LABELS = {
    navasan_auto: 'خودکار (نوسان)',
    navasan_manual: 'دستی — دریافت الان (نوسان)',
    admin_manual: 'دستی — ادمین'
  };

  const columns = [
    {
      title: 'تاریخ',
      dataIndex: 'createdAt',
      render: (v) => new Date(v).toLocaleString('fa-IR')
    },
    {
      title: 'منبع',
      dataIndex: 'source',
      render: (v) => SOURCE_LABELS[v] || v
    },
    {
      title: 'وضعیت',
      dataIndex: 'success',
      render: (v) => (v ? <Tag color="green">موفق</Tag> : <Tag color="red">ناموفق</Tag>)
    },
    {
      title: 'پوند (تومان)',
      dataIndex: 'tomanPerPound',
      render: (v) => (v ? Number(v).toLocaleString('fa-IR') : '-')
    },
    {
      title: 'دلار (تومان)',
      dataIndex: 'tomanPerUsd',
      render: (v) => (v ? Number(v).toLocaleString('fa-IR') : '-')
    },
    {
      title: 'خطا',
      dataIndex: 'errorMessage',
      render: (v) => v || '-'
    }
  ];

  return (
    <Card
      size="small"
      title={<Space><HistoryOutlined /> گزارش تاریخچه‌ی نرخ ارز</Space>}
      style={{ marginTop: 20 }}
      extra={<Button icon={<ReloadOutlined />} onClick={() => load(pagination.page)}>تازه‌سازی</Button>}
    >
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={items}
        size="small"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          onChange: (page) => load(page)
        }}
      />
    </Card>
  );
}

// ─── تب ۲: آمار بانک ──────────────────────────────────────────────────────────
function BankStatsTab() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getBankStats();
      if (res.data.success) setStats(res.data.stats);
    } catch {
      message.error('خطا در دریافت آمار بانک');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button icon={<ReloadOutlined />} onClick={load} style={{ marginBottom: 16 }}>
        تازه‌سازی
      </Button>
      <Spin spinning={loading}>
        {stats && (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small"><Statistic title="کل خزانه‌ها" value={stats.vaults.total} /></Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="خزانه‌های قفل‌شده"
                    value={stats.vaults.locked}
                    valueStyle={stats.vaults.locked ? { color: '#cf1322' } : undefined}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small"><Statistic title="کل تراکنش‌ها" value={stats.transactions.total} /></Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="تراکنش‌های در انتظار"
                    value={stats.transactions.pending}
                    valueStyle={stats.transactions.pending ? { color: '#d48806' } : undefined}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="مجموع پول در گردش" style={{ marginTop: 20 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic title="موجودی خزانه‌ها" value={stats.totalSupply.vaultDisplay} />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic title="موجودی کیف‌پول‌ها" value={stats.totalSupply.walletDisplay} />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="مجموع کل"
                    value={stats.totalSupply.totalDisplay}
                    valueStyle={{ fontWeight: 'bold', color: '#722f37' }}
                  />
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
}

// ─── تب ۳: خزانه‌ها ───────────────────────────────────────────────────────────
function VaultsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lockedFilter, setLockedFilter] = useState(undefined);

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [lockModal, setLockModal] = useState(null);
  const [lockReason, setLockReason] = useState('');

  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustForm] = Form.useForm();
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, lockedFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (lockedFilter !== undefined) filters.isLocked = lockedFilter;
      const res = await adminAPI.getBankVaults(page, 20, filters);
      if (res.data.success) {
        setItems(res.data.vaults || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch {
      message.error('خطا در دریافت خزانه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetail({ loading: true, userRecord: record });
    try {
      const res = await adminAPI.getBankVault(record.user._id);
      if (res.data.success) setDetail({ ...res.data, loading: false, userRecord: record });
    } catch {
      message.error('خطا در دریافت جزئیات خزانه');
      setDetailOpen(false);
    }
  };

  const submitLock = async () => {
    try {
      const res = await adminAPI.lockBankVault(lockModal.user._id, lockReason);
      if (res.data.success) {
        message.success('خزانه قفل شد');
        setLockModal(null);
        setLockReason('');
        load();
      } else {
        message.error(res.data.message || 'خطا در قفل کردن خزانه');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در قفل کردن خزانه');
    }
  };

  const handleUnlock = async (record) => {
    try {
      const res = await adminAPI.unlockBankVault(record.user._id);
      if (res.data.success) {
        message.success('خزانه باز شد');
        load();
      } else {
        message.error(res.data.message || 'خطا در باز کردن خزانه');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در باز کردن خزانه');
    }
  };

  const openAdjust = (record) => {
    setAdjustModal(record);
    adjustForm.resetFields();
    adjustForm.setFieldsValue({ target: 'wallet', direction: 'credit', galleons: 0, sickles: 0, knuts: 0 });
  };

  const submitAdjust = async () => {
    try {
      const values = await adjustForm.validateFields();
      const totalAmount =
        (values.galleons || 0) * KNUTS_PER_GALLEON +
        (values.sickles || 0) * KNUTS_PER_SICKLE +
        (values.knuts || 0);
      if (totalAmount <= 0) {
        message.error('مبلغ باید بزرگتر از صفر باشد');
        return;
      }
      const signedKnuts = values.direction === 'debit' ? -totalAmount : totalAmount;
      setAdjustSubmitting(true);
      const res = await adminAPI.adjustBankBalance(adjustModal.user._id, {
        knuts: signedKnuts,
        target: values.target,
        description: values.description
      });
      if (res.data.success) {
        message.success('تراکنش با موفقیت ثبت شد');
        setAdjustModal(null);
        load();
      } else {
        message.error(res.data.message || 'خطا در ثبت تراکنش');
      }
    } catch (e) {
      if (e?.errorFields) return; // خطای اعتبارسنجی فرم — قبلاً نمایش داده شده
      message.error(e?.response?.data?.message || 'خطا در ثبت تراکنش');
    } finally {
      setAdjustSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'کاربر',
      key: 'user',
      render: (_, r) => (
        <div>
          <strong>{r.user?.firstName} {r.user?.lastName}</strong>
          <div style={{ color: '#888', fontSize: 12 }}>{r.user?.phoneNumber}</div>
        </div>
      )
    },
    { title: 'شماره خزانه', dataIndex: 'vaultNumber', width: 110 },
    { title: 'گابلین مسئول', dataIndex: 'governingGoblin', width: 120 },
    {
      title: 'موجودی خزانه',
      key: 'vaultBalance',
      render: (_, r) => formatKnuts(r.vaultKnuts)
    },
    {
      title: 'موجودی کیف‌پول',
      key: 'walletBalance',
      render: (_, r) => formatKnuts(r.user?.walletKnuts || 0)
    },
    {
      title: 'وضعیت',
      key: 'locked',
      width: 100,
      render: (_, r) =>
        r.isLocked ? (
          <Tag color="red" icon={<LockOutlined />}>قفل</Tag>
        ) : (
          <Tag color="green" icon={<UnlockOutlined />}>باز</Tag>
        )
    },
    {
      title: 'آخرین تراکنش',
      key: 'lastTx',
      width: 150,
      render: (_, r) => (r.lastTransactionAt ? new Date(r.lastTransactionAt).toLocaleString('fa-IR') : '-')
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 280,
      render: (_, r) => (
        <Space size="small" wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>مشاهده</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openAdjust(r)}>تعدیل</Button>
          {r.isLocked ? (
            <Popconfirm
              title="آیا از باز کردن این خزانه اطمینان دارید؟"
              onConfirm={() => handleUnlock(r)}
              okText="بله"
              cancelText="خیر"
            >
              <Button size="small" icon={<UnlockOutlined />}>باز کردن</Button>
            </Popconfirm>
          ) : (
            <Button size="small" danger icon={<LockOutlined />} onClick={() => setLockModal(r)}>قفل کردن</Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="فیلتر وضعیت"
          style={{ width: 180 }}
          value={lockedFilter}
          onChange={(v) => { setLockedFilter(v); setPage(1); }}
          options={[
            { label: 'قفل‌شده', value: 'true' },
            { label: 'باز', value: 'false' }
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ current: page, pageSize: 20, total, onChange: setPage, showSizeChanger: false }}
      />

      {/* مودال مشاهده جزئیات */}
      <Modal
        title="جزئیات خزانه"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[<Button key="close" onClick={() => setDetailOpen(false)}>بستن</Button>]}
        width={560}
      >
        {detail?.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : detail ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="کاربر">
              {detail.userRecord?.user?.firstName} {detail.userRecord?.user?.lastName} ({detail.userRecord?.user?.phoneNumber})
            </Descriptions.Item>
            <Descriptions.Item label="شماره خزانه">{detail.vault?.vaultNumber}</Descriptions.Item>
            <Descriptions.Item label="شماره کلید">{detail.vault?.keyNumber}</Descriptions.Item>
            <Descriptions.Item label="سطح امنیتی">{detail.vault?.securityLevel}</Descriptions.Item>
            <Descriptions.Item label="گابلین مسئول">{detail.vault?.governingGoblin}</Descriptions.Item>
            <Descriptions.Item label="موجودی خزانه">{detail.vaultDisplay}</Descriptions.Item>
            <Descriptions.Item label="موجودی کیف‌پول">{detail.wallet?.display}</Descriptions.Item>
            <Descriptions.Item label="تاریخ افتتاح">
              {detail.vault?.openedAt ? new Date(detail.vault.openedAt).toLocaleString('fa-IR') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="آخرین تراکنش">
              {detail.vault?.lastTransactionAt ? new Date(detail.vault.lastTransactionAt).toLocaleString('fa-IR') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="وضعیت">
              {detail.vault?.isLocked ? (
                <Tag color="red">قفل — {detail.vault.lockReason || 'بدون دلیل ثبت‌شده'}</Tag>
              ) : (
                <Tag color="green">باز</Tag>
              )}
            </Descriptions.Item>
            {detail.vault?.safeDepositItems?.length > 0 && (
              <Descriptions.Item label="اقلام صندوق امانات">
                <Space wrap>
                  {detail.vault.safeDepositItems.map((item) => (
                    <Tag key={item._id}>{item.name}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : null}
      </Modal>

      {/* مودال قفل کردن */}
      <Modal
        title={lockModal ? `قفل کردن خزانه — ${lockModal.user?.firstName} ${lockModal.user?.lastName}` : ''}
        open={!!lockModal}
        onCancel={() => { setLockModal(null); setLockReason(''); }}
        onOk={submitLock}
        okText="قفل کن"
        okButtonProps={{ danger: true }}
        cancelText="انصراف"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          پس از قفل شدن، کاربر نمی‌تواند بین خزانه و کیف‌پول خود انتقالی انجام دهد.
        </Text>
        <Input.TextArea
          rows={3}
          placeholder="دلیل قفل کردن (اختیاری)"
          value={lockReason}
          onChange={(e) => setLockReason(e.target.value)}
        />
      </Modal>

      {/* مودال تعدیل دستی موجودی */}
      <Modal
        title={adjustModal ? `تعدیل دستی موجودی — ${adjustModal.user?.firstName} ${adjustModal.user?.lastName}` : ''}
        open={!!adjustModal}
        onCancel={() => setAdjustModal(null)}
        onOk={submitAdjust}
        confirmLoading={adjustSubmitting}
        okText="ثبت تراکنش"
        cancelText="انصراف"
      >
        <Form form={adjustForm} layout="vertical">
          <Form.Item name="target" label="هدف" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="wallet">کیف پول</Radio.Button>
              <Radio.Button value="vault">خزانه</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="direction" label="نوع عملیات" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="credit">واریز (افزایش)</Radio.Button>
              <Radio.Button value="debit">برداشت (کاهش)</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Space size="middle">
            <Form.Item name="galleons" label="گالیون"><InputNumber min={0} style={{ width: 100 }} /></Form.Item>
            <Form.Item name="sickles" label="سیکل"><InputNumber min={0} max={16} style={{ width: 100 }} /></Form.Item>
            <Form.Item name="knuts" label="نات"><InputNumber min={0} max={28} style={{ width: 100 }} /></Form.Item>
          </Space>
          <Form.Item
            name="description"
            label="توضیحات"
            rules={[{ required: true, message: 'توضیح تراکنش را وارد کنید' }]}
          >
            <Input placeholder="مثلاً: جبران خطای فنی" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ─── تب ۴: تراکنش‌ها ──────────────────────────────────────────────────────────
function TransactionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [gateway, setGateway] = useState(undefined);
  const [site, setSite] = useState(undefined);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, status, gateway, site]);

  const load = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (type) filters.type = type;
      if (status) filters.status = status;
      if (gateway) filters.gateway = gateway;
      if (site) filters.site = site;
      const res = await adminAPI.getAllTransactions(page, 20, filters);
      if (res.data.success) {
        setItems(res.data.transactions || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch {
      message.error('خطا در دریافت تراکنش‌ها');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'کاربر',
      key: 'user',
      render: (_, r) =>
        r.user ? (
          <div>
            <strong>{r.user.firstName} {r.user.lastName}</strong>
            <div style={{ color: '#888', fontSize: 12 }}>{r.user.phoneNumber}</div>
          </div>
        ) : (
          <span style={{ color: '#aaa' }}>-</span>
        )
    },
    {
      title: 'نوع',
      dataIndex: 'type',
      width: 180,
      render: (t) => TX_TYPE_LABELS[t] || t
    },
    {
      title: 'مبلغ',
      key: 'amount',
      width: 170,
      render: (_, r) => (
        <div>
          {r.amount > 0 && <div>{Number(r.amount).toLocaleString('fa-IR')} تومان</div>}
          {r.amountKnuts != null && (
            <div style={{ color: '#722f37' }}>
              {formatKnuts(Math.abs(r.amountKnuts))}{r.amountKnuts < 0 ? ' (برداشت)' : ''}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      width: 110,
      render: (s) => <Tag color={STATUS_LABELS[s]?.color || 'default'}>{STATUS_LABELS[s]?.text || s}</Tag>
    },
    {
      title: 'درگاه',
      dataIndex: 'gateway',
      width: 110,
      render: (g) => <Tag color={g === 'oxapay' ? 'gold' : g === 'zibal' ? 'purple' : 'blue'}>{GATEWAY_LABELS[g] || g || '-'}</Tag>
    },
    {
      title: 'سایت',
      dataIndex: 'site',
      width: 130,
      render: (s) => <Tag color={s === 'international' ? 'green' : 'default'}>{s === 'international' ? 'بین‌المللی (potterheads.org)' : 'ایران (potterhead.ir)'}</Tag>
    },
    { title: 'توضیحات', dataIndex: 'description', ellipsis: true },
    {
      title: 'تاریخ',
      dataIndex: 'createdAt',
      width: 150,
      render: (d) => (d ? new Date(d).toLocaleString('fa-IR') : '-')
    }
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="نوع تراکنش"
          style={{ width: 220 }}
          value={type}
          onChange={(v) => { setType(v); setPage(1); }}
          options={Object.entries(TX_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="وضعیت"
          style={{ width: 160 }}
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          options={Object.entries(STATUS_LABELS).map(([value, meta]) => ({ value, label: meta.text }))}
        />
        <Select
          allowClear
          placeholder="درگاه"
          style={{ width: 160 }}
          value={gateway}
          onChange={(v) => { setGateway(v); setPage(1); }}
          options={Object.entries(GATEWAY_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="سایت"
          style={{ width: 190 }}
          value={site}
          onChange={(v) => { setSite(v); setPage(1); }}
          options={[
            { value: 'ir', label: 'ایران (potterhead.ir)' },
            { value: 'international', label: 'بین‌المللی (potterheads.org)' }
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ current: page, pageSize: 20, total, onChange: setPage, showSizeChanger: false }}
      />
    </div>
  );
}
